import React, { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { SearchInputSmall } from '../common/SearchInputSmall';
import { checkRoles, CLAIMS } from '../../utils/roles-helper';
import { CheckPicker, Dropdown, SelectPicker } from 'rsuite';
import { FilterComponent } from '../FilterComponent';
import clsx from 'clsx';
import { RenderExtraFooter } from '../filter/RenderExtraFooter';
import { ButtonComponent } from '../ButtonComponent';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { IAuthState } from '../../redux/AuthRedux';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from '../../../setup';
import { IApplicationFilters } from '../../store/merchnts/actions/application';
import { useMerchants } from '../../store/merchnts/merchants-context';
import { PageSummaryComponent } from '../PageSummaryComponent';
import {
  APPLICATIONS_FILTER_STATUSES,
} from '../../utils/applicationsListUtils';
import { useLocation } from 'react-router';
import { makeSearchParams, parseSearchParams } from '../../utils/utilities';
import { useDebounce } from 'use-debounce';
import { getTableSortField, getTableSort } from '../../utils/linksHelper';
import { MRT_SortingState } from 'material-react-table';

export const applicationTypes = [
  {
    name: 'Quick application',
    path: 'quick-application',
    actionName: 'Quick edit'
  },
  {
    name: 'Regular application',
    path: 'new-application',
    actionName: 'Edit'
  }
];

interface Props {
  statuses: string[];
  setStatuses: (value: string[]) => void;
  riskStatuses: string[];
  setRiskStatuses: (value: string[]) => void;
  operationsStatuses: string[];
  setOperationsStatuses: (value: string[]) => void;
  query: string;
  setQuery: (value: string) => void;
  partnerId: string;
  setPartnerId: (value: string) => void;
  integrationPartnerId: string;
  setIntegrationPartnerId: (value: string) => void;
  sort: MRT_SortingState;
}

export const ApplicationsListHeader: FC<Props> = ({
  statuses,
  setStatuses,
  riskStatuses,
  setRiskStatuses,
  operationsStatuses,
  setOperationsStatuses,
  query,
  setQuery,
  partnerId,
  setPartnerId,
  integrationPartnerId,
  setIntegrationPartnerId,
  sort
}) => {
  const { t } = useTranslation();
  const isMounted = useRef(false);

  const authUser: IAuthState = useSelector<RootState>(({ auth }) => auth, shallowEqual);
  const { pathname, search } = useLocation();
  const navigate = useNavigate();

  const savedApplicationFilters: IApplicationFilters = parseSearchParams(search) || {};

  const picker = useRef(null);
  const operationsStatusRef = useRef(null);
  const riskStatusRef = useRef(null);

  const { merchantState } = useMerchants();
  const {
    merchantsLists,
    applicationSummary,
    whitelabelPartnersListSelector,
    integrationPartnersListSelector,
  } = merchantState;

  const [searchValue, setSearchValue] = useState<string>(savedApplicationFilters?.query ?? query);

  const [value] = useDebounce(searchValue.toLowerCase().trim(), 1000);

  useEffect(() => {
    if (!isMounted.current) return;

    const params: IApplicationFilters = { ...savedApplicationFilters };

    params.query = searchValue;
    params.partnerId = partnerId;
    params.integrationPartnerId = integrationPartnerId;
    params.orderBy = getTableSort(sort);
    params.sortField = getTableSortField(sort);
    params.pageNumber = 1;

    navigate(`${pathname}?${makeSearchParams(params)}`);
  }, [value, partnerId, sort, integrationPartnerId]);

  useEffect(() => {
    if (!isMounted.current) {
      // first mount
      isMounted.current = true;
    }
  }, []);

  const newMerchantList = [...merchantsLists.applicationStatuses];
  const index = merchantsLists.applicationStatuses.findIndex(el => el.label.toLowerCase() === 'approved' && el);
  newMerchantList.splice(index, 1);

  const getAllValue = (name: APPLICATIONS_FILTER_STATUSES): string[] => {
    switch (name) {
      case APPLICATIONS_FILTER_STATUSES.APPLICATION_STATUS:
        return newMerchantList.map(item => item.value);
      case APPLICATIONS_FILTER_STATUSES.OPERATIONS_STATUS:
        return merchantsLists.applicationOperationsStatuses.map(item => item.value);
      case APPLICATIONS_FILTER_STATUSES.RISK_STATUS:
        return merchantsLists.applicationRiskStatuses.map(item => item.value);
    }
  };

  const handleChange = (value: string[], name: APPLICATIONS_FILTER_STATUSES): void => {
    switch (name) {
      case APPLICATIONS_FILTER_STATUSES.APPLICATION_STATUS:
        setStatuses(value);
        break;
      case APPLICATIONS_FILTER_STATUSES.OPERATIONS_STATUS:
        setOperationsStatuses(value);
        break;
      case APPLICATIONS_FILTER_STATUSES.RISK_STATUS:
        setRiskStatuses(value);
        break;
    }
  };

  const handleCheckAll = (value, checked: boolean, name: APPLICATIONS_FILTER_STATUSES): void => {
    const statusValues = checked ? getAllValue(name) : [];
    switch (name) {
      case APPLICATIONS_FILTER_STATUSES.APPLICATION_STATUS:
        setStatuses(statusValues);
        break;
      case APPLICATIONS_FILTER_STATUSES.OPERATIONS_STATUS:
        setOperationsStatuses(statusValues);
        break;
      case APPLICATIONS_FILTER_STATUSES.RISK_STATUS:
        setRiskStatuses(statusValues);
        break;
    }
  };

  const handleClean = (name: APPLICATIONS_FILTER_STATUSES): void => {
    switch (name) {
      case APPLICATIONS_FILTER_STATUSES.APPLICATION_STATUS:
        setStatuses([]);
        break;
      case APPLICATIONS_FILTER_STATUSES.OPERATIONS_STATUS:
        setOperationsStatuses([]);
        break;
      case APPLICATIONS_FILTER_STATUSES.RISK_STATUS:
        setRiskStatuses([]);
        break;
    }

    const params = { ...savedApplicationFilters };
    params[name] = [];

    navigate(`${pathname}?${makeSearchParams(params)}`);
  };

  const handleSave = async (name: APPLICATIONS_FILTER_STATUSES): Promise<void> => {
    const params = { ...savedApplicationFilters };

    switch (name) {
      case APPLICATIONS_FILTER_STATUSES.APPLICATION_STATUS:
        picker.current?.close();
        params[APPLICATIONS_FILTER_STATUSES.APPLICATION_STATUS] = statuses;
        break;
      case APPLICATIONS_FILTER_STATUSES.OPERATIONS_STATUS:
        operationsStatusRef.current?.close();
        params[APPLICATIONS_FILTER_STATUSES.OPERATIONS_STATUS] = operationsStatuses;
        break;
      case APPLICATIONS_FILTER_STATUSES.RISK_STATUS:
        riskStatusRef.current?.close();
        params[APPLICATIONS_FILTER_STATUSES.RISK_STATUS] = riskStatuses;
        break;
    }

    navigate(`${pathname}?${makeSearchParams(params)}`);
  };

  const handleSearch = (value: string): void => {
    setSearchValue(value);
    setQuery(value);
  };

  const renderButton = (props, ref): JSX.Element => {
    return (
      <button
        {...props}
        ref={ref}
        className="btn-primary btn fw-bolder letter-spacing"
      >
        {t('APPLICATION_LIST.BUTTON_NAME')}
      </button>
    );
  };


  return (
    <>
      <PageSummaryComponent componentTitle="Applications Summary" data={applicationSummary}/>

      <div className="card bg-transparent">
        <div className="card-body px-0 py-0">
          <div className="d-sm-flex justify-content-between align-items-center mb-4">
            <div className="d-flex align-items-center block-width application-selector application-additional-class">
              <SearchInputSmall
                placeholder={t('APPLICATION_LIST.SEARCH_PLACEHOLDER')}
                additionalClass=""
                searchValue={searchValue}
                setSearchValue={handleSearch}
              />
            </div>
            {
              checkRoles(authUser, CLAIMS.SUPER_ADMIN_AND_ONLY_PARTNER_ADMIN) && (
                <Dropdown renderToggle={renderButton} placement="bottomEnd" className="application-dropdown">
                  {
                    applicationTypes.map(({ name, path }) => (
                      <Dropdown.Item
                        key={name}
                        onClick={(): void => navigate(`/accounts/applications/${path}`)}
                      >
                        {name}
                      </Dropdown.Item>
                    ))
                  }
                </Dropdown>
              )
            }
          </div>
          <div className="d-flex align-items-center fc-filter-line">
            {
              checkRoles(authUser, CLAIMS.SUPER_ADMIN) && (
                <FilterComponent value={partnerId} label={t('MERCHANTS_LIST.WHITELABEL_PARTNERS')} labelPlacement="integrated">
                  <SelectPicker
                    data={whitelabelPartnersListSelector}
                    appearance="subtle"
                    placement="bottomStart"
                    placeholder={t('MERCHANTS_LIST.WHITELABEL_PARTNERS')}
                    onSelect={(pid): void => setPartnerId(pid)}
                    className={clsx('fc-selector timezone-selector', !partnerId ? 'fc-added-icon' : 'fc-selector-cleanable')}
                    onClean={(): void => setPartnerId('')}
                    // searchable={false}
                    defaultValue={savedApplicationFilters?.partnerId}
                  />
                </FilterComponent>
              )
            }
            {
              checkRoles(authUser, CLAIMS.SUPER_ADMIN_AND_ONLY_PARTNER_ADMIN) && (
                <FilterComponent value={integrationPartnerId} label={t('MERCHANTS_LIST.INTEGRATION_PARTNERS')} labelPlacement="integrated">
                  <SelectPicker
                    data={integrationPartnersListSelector}
                    appearance="subtle"
                    placement="bottomStart"
                    placeholder={t('MERCHANTS_LIST.INTEGRATION_PARTNERS')}
                    onSelect={(pid): void => setIntegrationPartnerId(pid)}
                    className={clsx('fc-selector timezone-selector', !integrationPartnerId ? 'fc-added-icon' : 'fc-selector-cleanable')}
                    onClean={(): void => setIntegrationPartnerId('')}
                    // searchable={false}
                    defaultValue={savedApplicationFilters?.integrationPartnerId}
                  />
                </FilterComponent>
              )
            }
            <FilterComponent value={statuses?.length} label={t('APPLICATION_LIST.STATUS_PLACEHOLDER')} labelPlacement="integrated">
              <CheckPicker
                data={newMerchantList}
                placeholder={t('APPLICATION_LIST.STATUS_PLACEHOLDER')}
                className={clsx('fc-selector picker-selector', !statuses?.length && 'fc-added-icon')}
                placement="bottomEnd"
                appearance="subtle"
                ref={picker}
                searchable={false}
                value={statuses}
                onChange={(value): void => handleChange(value, APPLICATIONS_FILTER_STATUSES.APPLICATION_STATUS)}
                onClean={(): void => handleClean(APPLICATIONS_FILTER_STATUSES.APPLICATION_STATUS)}
                renderExtraFooter={(): ReactNode => (
                  <RenderExtraFooter
                    statusFilter={statuses}
                    allValue={getAllValue(APPLICATIONS_FILTER_STATUSES.APPLICATION_STATUS)}
                    handleCheckAll={(value, checked): void => handleCheckAll(value, checked, APPLICATIONS_FILTER_STATUSES.APPLICATION_STATUS)}
                    handleSave={(): Promise<void> => handleSave(APPLICATIONS_FILTER_STATUSES.APPLICATION_STATUS)}
                  />
                )}
              />
            </FilterComponent>
            <FilterComponent value={operationsStatuses?.length} label={t('APPLICATION_LIST.OPERATIONS_STATUS_PLACEHOLDER')} labelPlacement="integrated">
              <CheckPicker
                data={merchantsLists.applicationOperationsStatuses}
                placeholder={t('APPLICATION_LIST.OPERATIONS_STATUS_PLACEHOLDER')}
                className={clsx('fc-selector picker-selector', !operationsStatuses?.length && 'fc-added-icon')}
                placement="bottomEnd"
                appearance="subtle"
                ref={operationsStatusRef}
                searchable={false}
                value={operationsStatuses}
                onChange={(value): void => handleChange(value, APPLICATIONS_FILTER_STATUSES.OPERATIONS_STATUS)}
                onClean={(): void => handleClean(APPLICATIONS_FILTER_STATUSES.OPERATIONS_STATUS)}
                renderExtraFooter={(): ReactNode => (
                  <RenderExtraFooter
                    statusFilter={operationsStatuses}
                    allValue={getAllValue(APPLICATIONS_FILTER_STATUSES.OPERATIONS_STATUS)}
                    handleCheckAll={(value, checked): void => handleCheckAll(value, checked, APPLICATIONS_FILTER_STATUSES.OPERATIONS_STATUS)}
                    handleSave={(): Promise<void> => handleSave(APPLICATIONS_FILTER_STATUSES.OPERATIONS_STATUS)}
                  />
                )}
              />
            </FilterComponent>
            <FilterComponent value={riskStatuses?.length} label={t('APPLICATION_LIST.RISK_STATUS_PLACEHOLDER')} labelPlacement="integrated">
              <CheckPicker
                data={merchantsLists.applicationRiskStatuses}
                placeholder={t('APPLICATION_LIST.RISK_STATUS_PLACEHOLDER')}
                className={clsx('fc-selector picker-selector', !riskStatuses?.length && 'fc-added-icon')}
                placement="bottomEnd"
                appearance="subtle"
                ref={riskStatusRef}
                searchable={false}
                value={riskStatuses}
                onChange={(value): void => handleChange(value, APPLICATIONS_FILTER_STATUSES.RISK_STATUS)}
                onClean={(): void => handleClean(APPLICATIONS_FILTER_STATUSES.RISK_STATUS)}
                renderExtraFooter={(): ReactNode => (
                  <RenderExtraFooter
                    statusFilter={riskStatuses}
                    allValue={getAllValue(APPLICATIONS_FILTER_STATUSES.RISK_STATUS)}
                    handleCheckAll={(value, checked): void => handleCheckAll(value, checked, APPLICATIONS_FILTER_STATUSES.RISK_STATUS)}
                    handleSave={(): Promise<void> => handleSave(APPLICATIONS_FILTER_STATUSES.RISK_STATUS)}
                  />
                )}
              />
            </FilterComponent>

          </div>
        </div>
        <ButtonComponent
          onButtonClick={(): void => navigate('/accounts/applications/new-application')}
          buttonName={t('APPLICATION_LIST.BUTTON_NAME')}
          additionalClass="w-100 my-4 d-block d-sm-none"
        />
      </div>
    </>
  );
};